/**
 * useTaxonomy Hook
 *
 * React Query hooks for managing taxonomy version state.
 * Part of the GO-Lab Taxonomy Management feature.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { taxonomyService } from '../services/taxonomyService';
import type {
  TaxonomyKeySummary,
  TaxonomyVersionSummary,
  TaxonomyVersionDetail,
  TaxonomyDiff,
  DashboardData,
  PaginatedTimeline,
  TaxonomyData,
  CreateDraftOptions,
} from '../types/taxonomy';

// =============================================================================
// Query Keys
// =============================================================================

/**
 * Query key factory for taxonomy queries.
 * Enables granular cache invalidation.
 */
export const taxonomyKeys = {
  all: ['taxonomy'] as const,

  keys: () => [...taxonomyKeys.all, 'keys'] as const,

  versions: (taxonomyKey: string) =>
    [...taxonomyKeys.all, taxonomyKey, 'versions'] as const,
  versionList: (taxonomyKey: string, filters?: { status?: string }) =>
    [...taxonomyKeys.versions(taxonomyKey), 'list', filters] as const,
  versionDetail: (taxonomyKey: string, id: string) =>
    [...taxonomyKeys.versions(taxonomyKey), 'detail', id] as const,

  live: (taxonomyKey: string) =>
    [...taxonomyKeys.all, taxonomyKey, 'live'] as const,
  dashboard: (taxonomyKey: string) =>
    [...taxonomyKeys.all, taxonomyKey, 'dashboard'] as const,
  timeline: (taxonomyKey: string) =>
    [...taxonomyKeys.all, taxonomyKey, 'timeline'] as const,
  diff: (taxonomyKey: string, a: string, b: string) =>
    [...taxonomyKeys.all, taxonomyKey, 'diff', a, b] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all available taxonomy keys
 */
export const useTaxonomyKeys = () =>
  useQuery<TaxonomyKeySummary[]>({
    queryKey: taxonomyKeys.keys(),
    queryFn: () => taxonomyService.listTaxonomyKeys(),
    staleTime: 2 * 60 * 1000,
  });

/**
 * Fetch the current live taxonomy version with full data
 */
export const useLiveVersion = (taxonomyKey: string) =>
  useQuery<TaxonomyVersionDetail>({
    queryKey: taxonomyKeys.live(taxonomyKey),
    queryFn: () => taxonomyService.getLiveVersion(taxonomyKey),
    staleTime: 2 * 60 * 1000,
    enabled: !!taxonomyKey,
  });

/**
 * Fetch a specific taxonomy version by ID
 */
export const useVersionDetail = (taxonomyKey: string, versionId: string) =>
  useQuery<TaxonomyVersionDetail>({
    queryKey: taxonomyKeys.versionDetail(taxonomyKey, versionId),
    queryFn: () => taxonomyService.getVersion(taxonomyKey, versionId),
    enabled: !!taxonomyKey && !!versionId,
  });

/**
 * List taxonomy versions with optional status filter
 */
export const useVersionList = (taxonomyKey: string, filters?: { status?: string }) =>
  useQuery<TaxonomyVersionSummary[]>({
    queryKey: taxonomyKeys.versionList(taxonomyKey, filters),
    queryFn: () => taxonomyService.listVersions(taxonomyKey, filters),
    staleTime: 30 * 1000,
    enabled: !!taxonomyKey,
  });

/**
 * Fetch taxonomy dashboard data
 */
export const useDashboard = (taxonomyKey: string) =>
  useQuery<DashboardData>({
    queryKey: taxonomyKeys.dashboard(taxonomyKey),
    queryFn: () => taxonomyService.getDashboard(taxonomyKey),
    staleTime: 60 * 1000,
    enabled: !!taxonomyKey,
  });

/**
 * Fetch audit event timeline
 */
const TIMELINE_PAGE_SIZE = 30;

export const useTimeline = (taxonomyKey: string) =>
  useInfiniteQuery<PaginatedTimeline>({
    queryKey: taxonomyKeys.timeline(taxonomyKey),
    queryFn: ({ pageParam = 0 }) =>
      taxonomyService.getTimeline(taxonomyKey, { limit: TIMELINE_PAGE_SIZE, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      return allPages.reduce((total, page) => total + page.items.length, 0);
    },
    enabled: !!taxonomyKey,
    staleTime: 30 * 1000,
  });

/**
 * Fetch structural diff between two taxonomy versions
 */
export const useVersionDiff = (
  taxonomyKey: string,
  versionAId: string,
  versionBId: string,
  enabled = true
) =>
  useQuery<TaxonomyDiff>({
    queryKey: taxonomyKeys.diff(taxonomyKey, versionAId, versionBId),
    queryFn: () => taxonomyService.getDiff(taxonomyKey, versionAId, versionBId),
    enabled: enabled && !!taxonomyKey && !!versionAId && !!versionBId,
    staleTime: 0,
    gcTime: 0,
  });

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new draft taxonomy version
 */
export const useCreateDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TaxonomyVersionSummary,
    Error,
    { taxonomyKey: string; options?: CreateDraftOptions }
  >({
    mutationFn: ({ taxonomyKey, options }) =>
      taxonomyService.createDraft(taxonomyKey, options),
    onSuccess: (_, { taxonomyKey }) => {
      toast.success('Draft created');
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create draft');
    },
  });
};

/**
 * Update a draft's taxonomy data
 */
export const useUpdateDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TaxonomyVersionSummary,
    Error,
    { taxonomyKey: string; versionId: string; taxonomyData: TaxonomyData }
  >({
    mutationFn: ({ taxonomyKey, versionId, taxonomyData }) =>
      taxonomyService.updateDraft(taxonomyKey, versionId, taxonomyData),
    onSuccess: (_, { taxonomyKey, versionId }) => {
      toast.success('Draft saved');
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.versionDetail(taxonomyKey, versionId) });
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.versionList(taxonomyKey) });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save draft');
    },
  });
};

/**
 * Deploy a version to live
 */
export const useDeployVersion = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TaxonomyVersionSummary,
    Error,
    { taxonomyKey: string; versionId: string; deploymentNote: string }
  >({
    mutationFn: ({ taxonomyKey, versionId, deploymentNote }) =>
      taxonomyService.deployVersion(taxonomyKey, versionId, deploymentNote),
    onSuccess: () => {
      toast.success('Version deployed to live');
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to deploy version');
    },
  });
};

/**
 * Rollback to a previous version
 */
export const useRollbackVersion = () => {
  const queryClient = useQueryClient();
  return useMutation<
    TaxonomyVersionSummary,
    Error,
    { taxonomyKey: string; versionId: string; note: string }
  >({
    mutationFn: ({ taxonomyKey, versionId, note }) =>
      taxonomyService.rollbackToVersion(taxonomyKey, versionId, note),
    onSuccess: () => {
      toast.success('Rolled back successfully');
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to rollback');
    },
  });
};

/**
 * Delete an undeployed draft
 */
export const useDeleteDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { taxonomyKey: string; versionId: string }>({
    mutationFn: ({ taxonomyKey, versionId }) =>
      taxonomyService.deleteDraft(taxonomyKey, versionId),
    onSuccess: () => {
      toast.success('Draft deleted');
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete draft');
    },
  });
};

/**
 * Bulk reclassify signals on stale taxonomy versions.
 * Fetches stale signal IDs in pages and feeds them to the bulk-reclassify endpoint.
 */
export const useBulkReclassifyStale = () => {
  const queryClient = useQueryClient();
  return useMutation<{ started: number; total: number }, Error, { taxonomyKey: string }>({
    mutationFn: async ({ taxonomyKey }) => {
      // Dynamic import to avoid circular dependency
      const { bulkReclassifySignals } = await import('../../../../utils/api');
      let offset = 0;
      let totalStarted = 0;
      let total = 0;
      let hasMore = true;
      while (hasMore) {
        const batch = await taxonomyService.getStaleSignals(taxonomyKey, { limit: 100, offset });
        total = batch.total;
        if (batch.signalIds.length > 0) {
          await bulkReclassifySignals(batch.signalIds);
          totalStarted += batch.signalIds.length;
        }
        hasMore = batch.hasMore;
        offset += 100;
      }
      return { started: totalStarted, total };
    },
    onSuccess: ({ started }, { taxonomyKey }) => {
      toast.success(`Reclassification started for ${started} signals`);
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.dashboard(taxonomyKey) });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start reclassification');
    },
  });
};
